import React, { useState } from "react";
import {
  ref,
  push,
  orderByChild,
  equalTo,
  get,
  query,
} from "firebase/database";
import { database } from "../firebase";
import { useNavigate } from "react-router-dom";
import styles from "./create.module.css";
import ConfirmationDialog from "./confirm"; // <-- Make sure path is correct

const organizations = ["COS", "CLA", "CIE", "COE"];

const RegistrationPage = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    organization: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState("");
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  // Validation functions
  const validateEmail = (email) => email.endsWith("@tup.edu.ph");
  const validatePhone = (phone) => /^\d{10}$/.test(phone);
  const validatePassword = (password) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/.test(password);

  const handleChange = (e) => {
    if (e.target.name === "phone") {
      // Only allow digits, max 10 characters
      const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
      setForm((prev) => ({
        ...prev,
        phone: digits,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [e.target.name]: e.target.value,
      }));
    }
    setError("");
    setSuccess("");
  };

  // Check if email already exists in Firebase
  const checkEmailExists = async (email) => {
    const usersRef = ref(database, "users");
    const emailQuery = query(usersRef, orderByChild("email"), equalTo(email));
    const snapshot = await get(emailQuery);
    return snapshot.exists();
  };

  const fetchAllUsers = async () => {
    const usersRef = ref(database, "users");
    const snapshot = await get(usersRef);
    if (!snapshot.exists()) return [];
    const usersObj = snapshot.val();
    return Object.values(usersObj);
  };

  const nextStep = async () => {
    setError("");
    if (step === 1) {
      if (!validateEmail(form.email)) {
        setError("Please use your TUP email.");
        return;
      }
      try {
        const allUsers = await fetchAllUsers();
        const exists = allUsers.some((user) => user.email === form.email);
        if (exists) {
          setError(
            "Email already registered. Please use a different email or login."
          );
          return;
        }
        // Only open OTP dialog if not already verified
        if (!otpVerified) {
          setOtpDialogOpen(true);
          return;
        }
      } catch {
        setError("Failed to validate email existence. Please try again.");
        return;
      }
    } else if (step === 2) {
      if (!form.firstName.trim() || !form.lastName.trim()) {
        setError("First Name and Last Name are required");
        return;
      }
    } else if (step === 3) {
      if (!form.organization) {
        setError("Please select an organization");
        return;
      }
    } else if (step === 4) {
      if (!validatePhone(form.phone)) {
        setError("Enter last 10 digits of your phone (e.g. 9XXXXXXXXX)");
        return;
      }
    }
    setStep(step + 1);
  };

  const handleOtpConfirm = () => {
    setOtpVerified(true);
    setOtpDialogOpen(false);
    setTimeout(() => {
      setStep(step + 1);
    }, 100);
  };

  const handleOtpClose = () => {
    setOtpDialogOpen(false);
    setOtpVerified(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!validatePassword(form.password)) {
      setError(
        "Password must be at least 8 characters, include uppercase, lowercase, number, and special character"
      );
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      const usersRef = ref(database, "users");
      const formattedPhone = "0" + form.phone; // save as 0XXXXXXXXXX
      const newUser = {
        email: form.email,
        first_name: form.firstName,
        last_name: form.lastName,
        organization: form.organization,
        phone: formattedPhone,
        password: form.password,
        registration_date: new Date().toISOString(),
      };

      await push(usersRef, newUser);
      setSuccess("Registration successful!");
      setForm({
        email: "",
        firstName: "",
        lastName: "",
        organization: "",
        phone: "",
        password: "",
        confirmPassword: "",
      });
      setStep(1);
      setOtpVerified(false);
      setTimeout(() => {
        navigate("/");
      }, 1500);
    } catch (err) {
      setError("Failed to register user: " + err.message);
    }
  };

  return (
    <div className={styles.outerWrapper}>
      <div className={styles.container}>
        <h2 className={styles.h2}>Register</h2>
        <form onSubmit={handleSubmit} className={styles.form}>
          {step === 1 && (
            <>
              <label className={styles.label}>Email:</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                autoFocus
                className={styles.input}
              />
              <button
                type="button"
                onClick={nextStep}
                className={styles.button}
                style={{ marginTop: 10 }}
              >
                Next
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <label className={styles.label}>First Name:</label>
              <input
                type="text"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                required
                autoFocus
                className={styles.input}
              />
              <label className={styles.label}>Last Name:</label>
              <input
                type="text"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                required
                className={styles.input}
              />
              <div className={styles.buttonsRow}>
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  className={`${styles.button} ${styles["button--secondary"]}`}
                  style={{ marginRight: 5 }}
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className={styles.button}
                >
                  Next
                </button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <label className={styles.label}>Organization:</label>
              <select
                name="organization"
                value={form.organization}
                onChange={handleChange}
                required
                autoFocus
                className={styles.input}
              >
                <option value="">-- Select Organization --</option>
                {organizations.map((org) => (
                  <option key={org} value={org}>
                    {org}
                  </option>
                ))}
              </select>
              <div className={styles.buttonsRow}>
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  className={`${styles.button} ${styles["button--secondary"]}`}
                  style={{ marginRight: 5 }}
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className={styles.button}
                >
                  Next
                </button>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <label className={styles.label}>Phone:</label>
              <div style={{ display: "flex", alignItems: "center" }}>
                <span className={styles.prefix} style={{ marginRight: 4 }}>
                  +63
                </span>
                <input
                  type="text"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  maxLength={10}
                  required
                  autoFocus
                  className={styles.input}
                  style={{ width: "100%" }}
                  placeholder="9XXXXXXXXX"
                />
              </div>
              <small className={styles.note}>
                Enter your phone number without the leading zero. It will be saved as <b>09XXXXXXXXX</b>.
              </small>
              <div className={styles.buttonsRow}>
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  className={`${styles.button} ${styles["button--secondary"]}`}
                  style={{ marginRight: 5 }}
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className={styles.button}
                >
                  Next
                </button>
              </div>
            </>
          )}

          {step === 5 && (
            <>
              <label className={styles.label}>Password:</label>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={form.password}
                onChange={handleChange}
                required
                autoFocus
                className={styles.input}
              />
              <label className={styles.label}>Confirm Password:</label>
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                required
                className={styles.input}
              />
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={showPassword}
                  onChange={() => setShowPassword(!showPassword)}
                />
                Show Password
              </label>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={showConfirmPassword}
                  onChange={() => setShowConfirmPassword(!showConfirmPassword)}
                />
                Show Confirm Password
              </label>
              <div className={styles.buttonsRow}>
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  className={`${styles.button} ${styles["button--secondary"]}`}
                  style={{ marginRight: 5 }}
                >
                  Back
                </button>
                <button type="submit" className={styles.button}>
                  Create Account
                </button>
              </div>
            </>
          )}

          <button
            type="button"
            onClick={() => navigate("/login")}
            className={`${styles.button} ${styles["button--secondary"]}`}
            style={{ marginTop: 20 }}
          >
            Back to Login
          </button>

          {error && <div className={styles.error}>{error}</div>}
          {success && <div className={styles.success}>{success}</div>}
        </form>
        <ConfirmationDialog
          open={otpDialogOpen}
          onClose={handleOtpClose}
          onConfirm={handleOtpConfirm}
          email={form.email}
        />
      </div>
    </div>
  );
};

export default RegistrationPage;